import React, { useState } from 'react';
import { StatusBadge } from './Badges';
import SafeIcon from '../common/SafeIcon';
import FlagNLAModal from './FlagNLAModal';
import Notification from './Admin/Notification';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { splitPartNumbers } from '../lib/partNumbers';
import { ShopStockLine } from './StockBadge';
import * as FiIcons from 'react-icons/fi';

const { FiTag, FiFlag, FiLayers, FiAlertTriangle } = FiIcons;

/**
 * Standard PN Helper: Prioritizes OEM PN as Source of Truth
 */
export const getDisplayPN = (part) => {
  if (part?.oem_pn && part.oem_pn.trim() !== '' && part.oem_pn.trim() !== ';') {
    return part.oem_pn.replace(/;$/, '');
  }
  if (part?.part_numbr && part.part_numbr.trim() !== '') {
    return part.part_numbr;
  }
  return 'No PN';
};

/** Column template shared by the results header and each row (desktop). */
export const PART_ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto_auto] md:grid-cols-[minmax(150px,1.1fr)_minmax(0,2fr)_minmax(0,1fr)_120px_120px_36px]';

const PartCard = ({ part, onOpenNewer }) => {
  const { nlaTickets, inventoryActive } = useAppContext();
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [notify, setNotify] = useState(null);

  const pendingTicket = nlaTickets.find(t => t.part_id === part.id && t.status === 'Pending');
  const isDiscontinued = part.status === 'Discontinued';
  
  const displayPN = getDisplayPN(part);
  const newerPNs = splitPartNumbers(part.newer).slice(0, 3);
  const internalPN = part.part_numbr;

  // Potential alternates calculation
  const altFields = ['b_pn', 'chlx_pn', 'f_pn', 'gem_pn', 'rs_pn', 'mm_pn', 'js_pn', 'k_pn', 'l_pn', 'm_pn', 'n_pn', 'q_pn'];
  const altCount = altFields.reduce((acc, field) => (part[field] ? acc + 1 : acc), 0);

  const getNLAProps = () => {
    if (isDiscontinued) return { icon: FiAlertTriangle, color: 'text-red-500 dark:text-red-400', title: 'Part is confirmed Discontinued/NLA', actionable: false };
    if (pendingTicket) return { icon: FiFlag, color: 'text-amber-500 dark:text-amber-400', title: 'NLA Flag Pending Review', actionable: false };
    return { icon: FiFlag, color: 'text-slate-400 dark:text-slate-600 hover:text-red-600 dark:hover:text-red-400', title: 'Flag this part as No Longer Available', actionable: true };
  };

  const nlaProps = getNLAProps();

  return (
    <div
      role="row"
      className={`${PART_ROW_GRID} group relative items-center gap-x-3 gap-y-1 px-3 sm:px-4 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 hover:bg-blue-50 dark:hover:bg-slate-800/60 border-l-[3px] ${
        isDiscontinued ? 'border-l-red-600' : 'border-l-transparent hover:border-l-blue-600'
      }`}
    >
      <div role="cell" className="order-1 md:order-none text-left min-w-0">
        <div className={`font-mono text-sm font-semibold truncate ${displayPN !== 'No PN' ? 'text-blue-800 dark:text-blue-300' : 'text-slate-400'}`}>
          {displayPN}
        </div>
        {internalPN && internalPN !== displayPN && (
          <div className="font-mono text-[11px] text-slate-500 truncate" title="Internal part number">
            Int. {internalPN}
          </div>
        )}
      </div>

      <div role="cell" className="order-4 col-span-3 md:order-none md:col-span-1 text-left min-w-0">
        <div className="text-sm font-semibold text-slate-900 dark:text-white leading-snug md:truncate">
          {part.descrip1 || displayPN}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {[part.appl_brand, part.descrip2].filter(Boolean).join(' · ')}
        </div>
        <div className="mt-0.5"><ShopStockLine inventory={part.inventory} active={inventoryActive} /></div>
        {newerPNs.length > 0 && (
          <div className={`text-xs font-semibold truncate ${isDiscontinued ? 'text-red-800 dark:text-red-300' : 'text-blue-800 dark:text-blue-300'}`} title={newerPNs.join(', ')}>
            {isDiscontinued ? 'Use instead' : 'Newer'}:{' '}
            {newerPNs.map((pn, i) => (
              <React.Fragment key={pn}>
                {i > 0 && ', '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenNewer?.(part, pn); }}
                  title={`Open ${pn}`}
                  className="font-mono underline decoration-dotted underline-offset-2 hover:decoration-solid"
                >
                  {pn}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
        {part.matched_models?.length > 0 && (
          <div className="text-xs text-blue-800 dark:text-blue-300 truncate" title={part.matched_models.join(', ')}>
            Fits <span className="font-mono font-semibold">{part.matched_models.join(', ')}</span>
          </div>
        )}
      </div>

      <div role="cell" className="order-5 col-span-3 md:order-none md:col-span-1 text-left text-xs text-slate-600 dark:text-slate-400 truncate flex items-center gap-1.5">
        {part.equip_type && (
          <>
            <SafeIcon icon={FiTag} className="w-3 h-3 shrink-0 text-slate-400" />
            <span className="truncate">{part.equip_type}</span>
          </>
        )}
      </div>

      <div role="cell" className="order-6 col-span-3 md:order-none md:col-span-1 text-left text-xs">
        {altCount > 0 ? (
          <span className="inline-flex items-center gap-1 font-semibold text-blue-700 dark:text-blue-300">
            <SafeIcon icon={FiLayers} className="w-3 h-3" />
            {altCount} {altCount === 1 ? 'alternate' : 'alternates'}
          </span>
        ) : (
          <span className="text-slate-400">No alternates</span>
        )}
      </div>

      <div role="cell" className="order-2 md:order-none text-left">
        <StatusBadge status={part.status} />
      </div>

      <div role="cell" className="order-3 md:order-none flex justify-end">
        {user && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (nlaProps.actionable) setShowModal(true);
            }}
            title={nlaProps.title}
            aria-label={nlaProps.title}
            className={`flex h-11 w-11 items-center justify-center md:h-auto md:w-auto md:p-1.5 ${nlaProps.actionable ? 'hover:bg-red-50 dark:hover:bg-red-500/10 cursor-pointer' : 'cursor-default'}`}
          >
            <SafeIcon icon={nlaProps.icon} className={`w-4 h-4 ${nlaProps.color}`} />
          </button>
        )}
      </div>

      {showModal && (
        <FlagNLAModal 
          part={part} 
          onClose={() => setShowModal(false)} 
          onSuccess={() => {
            setShowModal(false);
            setNotify("Flagged for review.");
          }} 
        />
      )}
      {notify && <Notification message={notify} onClose={() => setNotify(null)} />}
    </div>
  );
};

export default PartCard;